import {
  Briefcase,
  Calculator,
  CalendarDays,
  Check,
  Clock3,
  Coins,
  Contact,
  Copy,
  Gift,
  LogOut,
  RefreshCw,
  Settings2,
  ShieldAlert,
  UserCheck2,
  UserRound,
  Users,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SystemFeatureFlags } from "./types";
import { TabType, ModuleType } from "./types";
import { useLanguage, LanguageToggle } from "@/lib/i18n";
import { FontSizeToggle } from "./FontSizeToggle";
import { toast } from "sonner";

interface HomeHeaderProps {
  currentDisplayName: string;
  savedName: string;
  lineUserId?: string;
  lineDisplayName?: string;
  serverError?: string;
  isStatusLoading?: boolean;
  liffState: "loading" | "login" | "ready" | "error";
  liffError: string;
  isManager: boolean;
  executiveEntry: boolean;
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  inputName: string;
  setInputName: (name: string) => void;
  candidateNames: string[];
  handleSaveFirstTimeName: (name: string) => void;
  handleClearSavedUser: () => void;
  moduleMascot: {
    eyebrow: string;
    title: string;
    detail: string;
    image: string;
  } | null;
  showFirstTimeModal: boolean;
  entryModule: ModuleType | null;
  featureFlags?: SystemFeatureFlags;
}

export function HomeHeader({
  currentDisplayName,
  savedName,
  lineUserId,
  lineDisplayName,
  serverError,
  isStatusLoading,
  liffState,
  liffError,
  isManager,
  executiveEntry,
  activeTab,
  setActiveTab,
  inputName,
  setInputName,
  candidateNames,
  handleSaveFirstTimeName,
  handleClearSavedUser,
  moduleMascot,
  showFirstTimeModal,
  entryModule,
  featureFlags,
}: HomeHeaderProps) {
  const { t } = useLanguage();
  return (
    <>
      {/* Top Main Navigation Header */}
      <header className="main-nav-header sticky top-0 z-40 bg-white/80 backdrop-blur-lg border-b border-slate-200/80 mb-2 sm:mb-3 shadow-sm">
        <div className={`reference-header-inner dashboard-container mx-auto px-2 sm:px-5 py-2 sm:py-3 flex items-center justify-between gap-1.5 sm:gap-3 ${executiveEntry ? "dashboard-container-admin" : "dashboard-container-employee"}`}>
          {/* Logo & System Brand */}
          <div className="reference-brand flex items-center gap-1.5 sm:gap-2.5 min-w-0 shrink">
            <img
              src="/sirarund-logo.png"
              alt="Sirarund Construction"
              className="brand-logo h-9 sm:h-12 w-auto object-contain shrink-0 rounded-lg shadow-2xs"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <div className="reference-brand-copy min-w-0 hidden min-[400px]:block">
              <div className="flex items-center gap-1.5">
                <span className="font-black text-slate-900 tracking-tight text-sm sm:text-lg leading-none">
                  ศิรารันดร์
                </span>
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] sm:text-[10px] font-black text-emerald-900 border border-emerald-200 uppercase tracking-wide">
                  Check-In
                </span>
              </div>
              <p className="text-[11px] font-semibold text-slate-500 truncate hidden sm:block">
                บจก. ศิรารันดร์ คอนสตรัคชั่น จำกัด
              </p>
            </div>
          </div>

          {/* User Status / Action Button & Language Switcher */}
          <div className="reference-header-actions flex items-center gap-1.5 sm:gap-2 shrink-0">
            <FontSizeToggle className="reference-font-toggle" />
            <LanguageToggle className="reference-language-toggle" />

            {(savedName || currentDisplayName || (executiveEntry && isManager)) && (
              <div
                className="flex items-center gap-1 sm:gap-2 rounded-full bg-amber-50 pl-2.5 sm:pl-3.5 pr-1 sm:pr-1.5 py-0.5 sm:py-1 border border-amber-200 text-xs font-bold text-amber-950 shadow-xs max-w-[170px] sm:max-w-none"
                title={lineUserId ? `LINE User ID: ${lineUserId}\nคลิกเพื่อคัดลอก ID` : "ผู้ใช้งานปัจจุบัน"}
              >
                <button
                  type="button"
                  onClick={() => {
                    if (lineUserId) {
                      navigator.clipboard.writeText(lineUserId);
                      toast.success(`คัดลอก LINE ID เรียบร้อย: ${lineUserId}`);
                    }
                  }}
                  className="flex items-center gap-1 text-left truncate hover:opacity-80"
                >
                  <span className="truncate max-w-[70px] sm:max-w-[140px]">
                    {savedName || currentDisplayName || "พนักงาน"}
                  </span>
                  {lineUserId && (
                    <span className="hidden md:inline-block text-[10px] text-amber-800/80 font-mono bg-amber-100/80 px-1 rounded">
                      {lineUserId.slice(0, 6)}...{lineUserId.slice(-4)}
                    </span>
                  )}
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 sm:h-6 sm:w-6 rounded-full hover:bg-rose-100 text-slate-500 hover:text-rose-600 shrink-0"
                  onClick={handleClearSavedUser}
                  title="ล้างแคชผู้ใช้งานและปลดการผูกบัญชี LINE LIFF"
                >
                  <LogOut size={12} />
                </Button>
              </div>
            )}

            {executiveEntry && isManager && (
              <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-black text-amber-900 border border-amber-300 shadow-xs">
                <Zap size={13} className="fill-amber-600 text-amber-600" />{" "}
                {t.userRoleExecutive}
              </span>
            )}
          </div>
        </div>
      </header>

      <div className={`dashboard-container mx-auto px-3 sm:px-5 ${executiveEntry ? "dashboard-container-admin" : "dashboard-container-employee"}`}>
        {executiveEntry && (liffState === "loading" || liffState === "login" || (liffState === "ready" && isStatusLoading)) && (
          <Card className="glass-panel mb-6 rounded-3xl border-amber-200/80 p-8 text-center shadow-lg">
            <RefreshCw className="mx-auto mb-3 animate-spin text-amber-500" size={30} />
            <h2 className="text-lg font-black text-slate-900">กำลังยืนยันสิทธิ์ผู้ดูแลระบบ</h2>
            <p className="mt-1 text-sm font-semibold text-slate-500">ระบบจะเปิดแดชบอร์ดให้อัตโนมัติ</p>
          </Card>
        )}

        {/* LIFF Connection Error state: shown when LIFF fails to initialize or connect */}
        {executiveEntry && liffState === "error" && (
          <Card className="glass-panel mb-6 rounded-[2.5rem] border-amber-200/80 shadow-2xl overflow-hidden relative">
            <div className="h-2.5 bg-gradient-to-r from-amber-500 to-rose-500" />
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-600 shadow-md">
                  <ShieldAlert size={32} />
                </div>
                <div className="flex-1">
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-black text-amber-800 mb-1">
                    LINE LIFF CONNECTION
                  </span>
                  <h2 className="text-2xl sm:text-3xl font-black text-slate-900">
                    เชื่อมต่อ LINE LIFF ไม่สำเร็จ
                  </h2>
                  <p className="text-sm sm:text-base font-semibold text-slate-600 mt-1">
                    {liffError || "ไม่สามารถเชื่อมต่อกับ LINE SDK ได้ กรุณาเปิดผ่าน LINE หรือรีเฟรชหน้าเว็บอีกครั้ง"}
                  </p>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-slate-100 flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  className="h-12 px-5 rounded-full text-sm font-bold text-slate-700 border-slate-300 hover:bg-slate-100"
                  onClick={() => window.location.reload()}
                >
                  <RefreshCw size={16} className="mr-2" />
                  ลองใหม่อีกครั้ง (Reload)
                </Button>
                <Button
                  variant="outline"
                  className="h-12 px-5 rounded-full text-sm font-bold text-slate-600 border-slate-200 hover:bg-slate-50"
                  onClick={() => {
                    window.location.href = "/";
                  }}
                >
                  ไปยังหน้าลงเวลาพนักงานทั่วไป
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Unauthorized admin state: clearly informs user of who is logged in and how to grant access */}
        {executiveEntry && liffState === "ready" && !isStatusLoading && !isManager && (
          <Card className="glass-panel mb-6 rounded-[2.5rem] border-rose-200/80 shadow-2xl overflow-hidden relative">
            <div className="h-2.5 bg-gradient-to-r from-slate-900 via-rose-500 to-amber-500" />
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-tr from-slate-900 to-slate-800 text-amber-400 shadow-lg">
                  <ShieldAlert size={32} />
                </div>
                <div className="flex-1">
                  <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-3 py-1 text-xs font-black text-rose-800 mb-1">
                    EXECUTIVE & HR PORTAL
                  </span>
                  <h2 className="text-2xl sm:text-3xl font-black text-slate-900">
                    ไม่มีสิทธิ์เข้าถึงแดชบอร์ดผู้บริหาร
                  </h2>
                  <p className="text-sm sm:text-base font-semibold text-slate-600 mt-1">
                    บัญชี LINE นี้ยังไม่ได้ถูกตั้งค่าเป็นผู้ดูแลระบบ (Admin/Executive)
                    {liffError ? ` (${liffError})` : ""}
                  </p>
                </div>
              </div>

              {(lineUserId || lineDisplayName || serverError) && (
                <div className="mt-5 rounded-2xl bg-slate-50 border border-slate-200/80 p-4 text-xs sm:text-sm text-slate-700 space-y-2">
                  {lineDisplayName && (
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-500">บัญชี LINE ที่ใช้งาน:</span>
                      <span className="font-extrabold text-slate-900">{lineDisplayName}</span>
                    </div>
                  )}
                  {lineUserId && (
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-2 border-t border-slate-200/60">
                      <div>
                        <span className="font-bold text-slate-500">LINE User ID: </span>
                        <code className="bg-slate-200/70 text-slate-800 px-2 py-0.5 rounded font-mono text-xs select-all font-bold">
                          {lineUserId}
                        </code>
                      </div>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="h-8 px-3 text-xs font-bold rounded-lg border border-slate-300"
                        onClick={() => {
                          void navigator.clipboard.writeText(lineUserId);
                          toast.success("คัดลอก LINE User ID เรียบร้อย");
                        }}
                      >
                        <Copy size={13} className="mr-1.5" />
                        คัดลอก User ID
                      </Button>
                    </div>
                  )}
                  {serverError && (
                    <div className="pt-2 border-t border-rose-200/60 text-rose-600 font-semibold text-xs">
                      สถานะเซิร์ฟเวอร์: {serverError}
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4 p-3.5 rounded-2xl bg-amber-50/70 border border-amber-200/60 text-xs text-amber-900 leading-relaxed">
                💡 <strong>คำแนะนำ:</strong> นำ LINE User ID ด้านบนไปเพิ่มในตัวแปรระบบ <code>EXECUTIVE_LINE_USER_IDS</code> หรือ <code>LINE_ADMIN_USER_ID</code> ใน Cloudflare Pages หรือไฟล์ <code>.env</code> แล้วกดบันทึกเพื่อเปิดสิทธิ์
              </div>

              <div className="mt-6 pt-4 border-t border-slate-100 flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  className="h-12 sm:h-14 px-5 rounded-full text-sm sm:text-base font-bold text-slate-700 border-slate-300 hover:bg-slate-100"
                  onClick={() => {
                    if (window.liff) {
                      try {
                        window.liff.logout();
                      } catch {}
                    }
                    window.location.reload();
                  }}
                >
                  <LogOut size={16} className="mr-2" />
                  สลับบัญชี LINE / เข้าสู่ระบบใหม่
                </Button>
                <Button
                  variant="outline"
                  className="h-12 sm:h-14 px-5 rounded-full text-sm sm:text-base font-bold text-slate-600 border-slate-200 hover:bg-slate-50"
                  onClick={() => {
                    window.location.href = "/";
                  }}
                >
                  ไปยังหน้าลงเวลาพนักงานทั่วไป
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tab Navigation Menu - Antixor Segmented Pill Style */}
        {((executiveEntry && isManager) || (!executiveEntry && Boolean(savedName || currentDisplayName))) && (
          <div className={`employee-tabs ${executiveEntry ? "admin-tabs" : "staff-tabs reference-staff-tabs"} mb-4 sm:mb-6 grid gap-1.5 p-1.5 rounded-full bg-slate-100 border border-slate-200`}>
            {executiveEntry ? (
              <>
                <button
                  aria-label={t.tabLiveBoard}
                  title={t.tabLiveBoard}
                  onClick={() => { window.history.replaceState({}, "", "/admin/?module=live-board"); setActiveTab("live-board"); }}
                  className={`tab-pill flex items-center gap-2 rounded-full px-4.5 py-3 text-sm sm:text-base font-extrabold whitespace-nowrap transition-all ${
                    activeTab === "live-board"
                      ? "active text-white"
                      : "text-slate-600 hover:text-amber-600 hover:bg-white/60"
                  }`}
                >
                  <Users
                    size={18}
                    className={
                      activeTab === "live-board"
                        ? "text-white"
                        : "text-slate-400"
                    }
                  />
                  <span className="tab-label">{t.tabLiveBoard}</span>
                </button>

                <button
                  aria-label={t.tabPayroll}
                  title={t.tabPayroll}
                  onClick={() => { window.history.replaceState({}, "", "/admin/?module=payroll"); setActiveTab("payroll"); }}
                  className={`tab-pill flex items-center gap-2 rounded-full px-4.5 py-3 text-sm sm:text-base font-extrabold whitespace-nowrap transition-all ${
                    activeTab === "payroll"
                      ? "active text-white"
                      : "text-slate-600 hover:text-amber-600 hover:bg-white/60"
                  }`}
                >
                  <Calculator
                    size={18}
                    className={
                      activeTab === "payroll"
                        ? "text-white"
                        : "text-slate-400"
                    }
                  />
                  <span className="tab-label">{t.tabPayroll}</span>
                </button>

                {featureFlags?.enableSalaryAdvance !== false && <button
                  aria-label={t.tabAdvanceApprovals}
                  title={t.tabAdvanceApprovals}
                  onClick={() => { window.history.replaceState({}, "", "/admin/?module=advance-approvals"); setActiveTab("advance-approvals"); }}
                  className={`tab-pill flex items-center gap-2 rounded-full px-4.5 py-3 text-sm sm:text-base font-extrabold whitespace-nowrap transition-all ${
                    activeTab === "advance-approvals"
                      ? "active text-white"
                      : "text-slate-600 hover:text-amber-600 hover:bg-white/60"
                  }`}
                >
                  <Coins
                    size={18}
                    className={
                      activeTab === "advance-approvals"
                        ? "text-white"
                        : "text-slate-400"
                    }
                  />
                  <span className="tab-label">{t.tabAdvanceApprovals}</span>
                </button>}



                <button
                  aria-label={t.tabTeam}
                  title={t.tabTeam}
                  onClick={() => { window.history.replaceState({}, "", "/admin/?module=team-directory"); setActiveTab("team-directory"); }}
                  className={`tab-pill flex items-center gap-2 rounded-full px-4.5 py-3 text-sm sm:text-base font-extrabold whitespace-nowrap transition-all ${
                    activeTab === "team-directory"
                      ? "active text-white"
                      : "text-slate-600 hover:text-amber-600 hover:bg-white/60"
                  }`}
                >
                  <Contact
                    size={18}
                    className={
                      activeTab === "team-directory"
                        ? "text-white"
                        : "text-slate-400"
                    }
                  />
                  <span className="tab-label">{t.tabTeam}</span>
                </button>

                <button
                  aria-label={t.tabSettings}
                  title={t.tabSettings}
                  onClick={() => { window.history.replaceState({}, "", "/admin/?module=hr-settings"); setActiveTab("hr-settings"); }}
                  className={`tab-pill flex items-center gap-2 rounded-full px-4.5 py-3 text-sm sm:text-base font-extrabold whitespace-nowrap transition-all ${
                    activeTab === "hr-settings"
                      ? "active text-white"
                      : "text-slate-600 hover:text-amber-600 hover:bg-white/60"
                  }`}
                >
                  <Settings2
                    size={18}
                    className={
                      activeTab === "hr-settings"
                        ? "text-white"
                        : "text-slate-400"
                    }
                  />
                  <span className="tab-label">{t.tabSettings}</span>
                </button>

                <button
                  aria-label={t.tabTasks}
                  title={t.tabTasks}
                  onClick={() => { window.history.replaceState({}, "", "/admin/?module=tasks"); setActiveTab("tasks"); }}
                  className={`tab-pill flex items-center gap-2 rounded-full px-4.5 py-3 text-sm sm:text-base font-extrabold whitespace-nowrap transition-all ${
                    activeTab === "tasks"
                      ? "active text-white"
                      : "text-slate-600 hover:text-amber-600 hover:bg-white/60"
                  }`}
                >
                  <Zap
                    size={18}
                    className={
                      activeTab === "tasks"
                        ? "text-white"
                        : "text-slate-400"
                    }
                  />
                  <span className="tab-label">{t.tabTasks}</span>
                </button>

                <button
                  aria-label={t.tabWelfare}
                  title={t.tabWelfare}
                  onClick={() => { window.history.replaceState({}, "", "/admin/?module=welfare"); setActiveTab("welfare"); }}
                  className={`tab-pill flex items-center gap-2 rounded-full px-4.5 py-3 text-sm sm:text-base font-extrabold whitespace-nowrap transition-all ${
                    activeTab === "welfare"
                      ? "active text-white"
                      : "text-slate-600 hover:text-amber-600 hover:bg-white/60"
                  }`}
                >
                  <Gift
                    size={18}
                    className={
                      activeTab === "welfare"
                        ? "text-white"
                        : "text-slate-400"
                    }
                  />
                  <span className="tab-label">{t.tabWelfare}</span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => { window.history.pushState({}, "", "/?module=attendance"); setActiveTab("attendance"); }}
                  className={`tab-pill flex items-center gap-2 rounded-full px-4.5 py-3 text-sm sm:text-base font-extrabold whitespace-nowrap transition-all ${
                    activeTab === "attendance"
                      ? "active text-white"
                      : "text-slate-600 hover:text-amber-600 hover:bg-white/60"
                  }`}
                >
                  <Clock3
                    size={18}
                    className={
                      activeTab === "attendance"
                        ? "text-white"
                        : "text-slate-400"
                    }
                  />
                  <span className="tab-label">{t.tabAttendance}</span>
                </button>

                {featureFlags?.enableSalaryAdvance !== false && <button
                  onClick={() => { window.history.pushState({}, "", "/?module=salary-advance"); setActiveTab("salary-advance"); }}
                  className={`tab-pill flex items-center gap-2 rounded-full px-4.5 py-3 text-sm sm:text-base font-extrabold whitespace-nowrap transition-all ${
                    activeTab === "salary-advance"
                      ? "active text-white"
                      : "text-slate-600 hover:text-amber-600 hover:bg-white/60"
                  }`}
                >
                  <Coins
                    size={18}
                    className={
                      activeTab === "salary-advance"
                        ? "text-white"
                        : "text-slate-400"
                    }
                  />
                  <span className="tab-label">{t.tabAdvance}</span>
                </button>}



                <button
                  onClick={() => { window.history.pushState({}, "", "/?module=tasks"); setActiveTab("tasks"); }}
                  className={`tab-pill flex items-center gap-2 rounded-full px-4.5 py-3 text-sm sm:text-base font-extrabold whitespace-nowrap transition-all ${
                    activeTab === "tasks"
                      ? "active text-white"
                      : "text-slate-600 hover:text-amber-600 hover:bg-white/60"
                  }`}
                >
                  <Zap
                    size={18}
                    className={
                      activeTab === "tasks"
                        ? "text-white"
                        : "text-slate-400"
                    }
                  />
                  <span className="tab-label">{t.tabTasks}</span>
                </button>

                <button
                  onClick={() => { window.history.pushState({}, "", "/?module=welfare"); setActiveTab("welfare"); }}
                  className={`tab-pill flex items-center gap-2 rounded-full px-4.5 py-3 text-sm sm:text-base font-extrabold whitespace-nowrap transition-all ${
                    activeTab === "welfare"
                      ? "active text-white"
                      : "text-slate-600 hover:text-amber-600 hover:bg-white/60"
                  }`}
                >
                  <Gift
                    size={18}
                    className={
                      activeTab === "welfare"
                        ? "text-white"
                        : "text-slate-400"
                    }
                  />
                  <span className="tab-label">{t.tabWelfare}</span>
                </button>
              </>
            )}
          </div>
        )}

        {((executiveEntry && isManager) || (!executiveEntry && Boolean(savedName || currentDisplayName))) && moduleMascot && (
          <section
            className="module-hero mb-3 sm:mb-5"
            aria-label={moduleMascot.title}
          >
            <div className="module-hero-copy">
              <p>{moduleMascot.eyebrow}</p>
              <h2>{moduleMascot.title}</h2>
              <span>{moduleMascot.detail}</span>
            </div>
            <img src={moduleMascot.image} alt="Demo module assistant" />
          </section>
        )}
      </div>
    </>
  );
}
